import React, { useEffect, useState } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { TComponentDetailsProps, InnerComponentTypes } from '../../types/productSeriesDetailFeature.types';
import { adjustModalPositionBasedOnContent } from '../../../../utils/modal.utils';
import { targetLinkdsk } from '../../../../utils/targetDskLink';
import CTAButton, { ctaEnums } from '../../../@shared/ctaButton';
import CTAWithArrow from '../../../@shared/ctaWithArrow';
import ModalDialog from '../../../@shared/modalDialog';
import RoutableNotEditableLink from '../../../@shared/routableNotEditableLink';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import InnerSplitModules from '../innerSplitModules';
import InnerCards from '../innerCards';
import Banner from './banner';
import { windowService } from '../../../../utils/window.utils'
import { googleClickCtaEvent } from '../../../../utils/analytics';
import { appendRootToRichTextInternalLinks } from '../../../../utils/appendRootToRichTextInternalLinks.utils';
import { appendRootToInternalLink } from '../../../../utils/appendRootToInternalLink.utils';
import useScrollWithShadow from '../../../../hooks/useScrollWithShadow';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';

const ContentHubSeriesDetails = (props: WithTranslation & TComponentDetailsProps): JSX.Element => {
  const model = props.component;
  const { t, backgroundColor } = props;
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const hasInnerComponents = !!(model?.innerComponents && model?.innerComponents.length);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const { showShadowBottom, scrollElementRef, onScrollHandler } = useScrollWithShadow({ bottomBuffer: 2 });
  const [shadowWidth, setShadowWidth] = useState(0);

  useEffect(() => {
    function handleResize() {
      const newShadowWidth = scrollElementRef?.current?.clientWidth || 0;
      setShadowWidth(newShadowWidth);
    }

    handleResize();
    windowService().addEventListener('resize', handleResize);
    return (): void => windowService().removeEventListener('resize', handleResize);
  }, [scrollElementRef]);


  const showModal = (): void => {
    setModalIsOpen(true);
  };

  const closeModal = (): void => {
    setModalIsOpen(false);
  };

  const scrollDownOnClick = () => {
    scrollElementRef.current.scrollBy({
      top: 50,
      left: 0,
      behavior: 'smooth',
    });
  };

  return (
    <article className={`component-details ${props.className}`} onScroll={onScrollHandler} ref={scrollElementRef}>
      {isDesktop && showShadowBottom && (
        <div className='component-details__shadow-wrapper' style={{ width: shadowWidth }}>
          <div className="component-details__scroll-more-icon" onClick={scrollDownOnClick}>
            <Icon iconName="arrow-head-down" size={iconSize.dsk} color='medium-gray' extraClassName='component-details__scroll-more-icon--pointer' />
          </div>
        </div>
      )}
      {hasInnerComponents && (
        <ModalDialog
          isOpen={modalIsOpen}
          close={closeModal}
          className="series-features-modal"
          onAfterOpen={adjustModalPositionBasedOnContent}
        >
          {model?.layoutType?.toLowerCase() === InnerComponentTypes.Split ? (
            <InnerSplitModules backgroundColor={backgroundColor?.backgroundColorOverlaySplit} components={model?.innerComponents} />
          ) : (
            <InnerCards backgroundColor={backgroundColor?.backgroundColorOverlayCardColumn} components={model?.innerComponents} />
          )}
        </ModalDialog>
      )}
      <Banner
        component={model}
        mediaHeightClassName="component-details__media-height"
        mediaCarouselClassName="component-details__media-carousel"
      />
      <div className="component-details__content">
        <p className="component-details__title">{model?.title}</p>
        <RichText
          editable={false}
          field={{ value: appendRootToRichTextInternalLinks(model?.description) }}
          tag="div"
          className="component-details__text rich-text"
        />
        <div className="component-details__button-wrapper">
          {hasInnerComponents && (
            <CTAButton type={ctaEnums.primaryRed}>
              <button
                className="component-details__button"
                onClick={(): void => {
                  showModal();
                  googleClickCtaEvent('Button', model?.button?.label ?? t('cnhi-discoverMore'), model?.eyebrow);
                }}
              >
                {model?.button?.label || t('cnhi-discoverMore')}
              </button>
            </CTAButton>
          )}
          {model?.button1?.label ? (
            <CTAButton type={ctaEnums.primaryRed}>
              <RoutableNotEditableLink
                className="component-details__button"
                href={appendRootToInternalLink(model?.button1.url)}
                target={targetLinkdsk({ value: { href: model?.button1?.url, target: model?.button1?.target } }, isDesktop)}
                onClick={(): void => googleClickCtaEvent('Button', model?.button1?.label, model?.eyebrow)}
              >
                {model?.button1?.label}
              </RoutableNotEditableLink>
            </CTAButton>
          ) : null}
          {model?.button2?.label ? (
            <CTAWithArrow
              text={model?.button2?.label}
              href={appendRootToInternalLink(model?.button2.url) || ''}
              target={targetLinkdsk({ value: { href: model?.button2?.url, target: model?.button2?.target } }, isDesktop)}
              onClick={() => {
                googleClickCtaEvent('Button', model?.button2?.label ?? '', model?.eyebrow ?? '');
              }}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default withTranslation()(ContentHubSeriesDetails);
